<!--
  Board Properties Panel - Property Editor for DDC Board Layout

  Edits properties of selected controllers or accessories on the board
  Supports various input types with validation
-->

<template>
  <div class="board-properties-panel">
    <!-- Header -->
    <div class="panel-header">
      <div class="text-subtitle2 text-grey-9 q-pa-sm">
        <q-icon name="settings" class="q-mr-xs" />
        Properties
      </div>
    </div>

    <!-- Content -->
    <div class="panel-content">
      <!-- No Selection -->
      <div v-if="!selectedItem" class="no-selection q-pa-md text-center">
        <q-icon name="touch_app" size="4em" color="grey-5" />
        <div class="text-caption text-grey-7 q-mt-sm">
          Select an item on the board to edit properties
        </div>
      </div>

      <!-- Controller Properties -->
      <div v-else-if="selectedItem.type === 'controller'" class="item-properties">
        <q-form class="q-pa-md q-gutter-md">
          <!-- Controller Name -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="memory" class="q-mr-xs" />
              Controller
            </div>
            <q-input
              :model-value="selectedItem.name"
              label="Name"
              dense
              outlined
              :readonly="readonly"
              @update:model-value="updateProperty('name', $event)"
            >
              <template #prepend>
                <q-icon name="label" color="primary" size="xs" />
              </template>
            </q-input>
          </div>

          <!-- Equipment Served -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="hub" class="q-mr-xs" />
              Equipment Served
            </div>
            <div v-if="selectedItem.equipment_served_list?.length > 0">
              <q-chip
                v-for="eq in selectedItem.equipment_served_list"
                :key="eq.id"
                size="sm"
                color="primary"
                text-color="white"
                class="q-mr-xs q-mb-xs"
              >
                {{ eq.name }}
              </q-chip>
            </div>
            <div v-else-if="selectedItem.equipment_served" class="text-body2">
              {{ selectedItem.equipment_served }}
            </div>
            <div v-else class="text-caption text-grey-6 text-italic">
              No equipment assigned
            </div>
          </div>

          <!-- Position & Rotation Section -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="place" class="q-mr-xs" />
              Position & Rotation
            </div>
            <div class="row q-gutter-sm q-mb-sm">
              <q-input
                :model-value="Math.round(selectedItem.position?.x || 0)"
                label="X (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updatePosition('x', parseInt($event))"
              />
              <q-input
                :model-value="Math.round(selectedItem.position?.y || 0)"
                label="Y (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updatePosition('y', parseInt($event))"
              />
            </div>
            <div class="row items-center q-gutter-sm">
              <q-btn-group flat>
                <q-btn
                  icon="rotate_left"
                  flat
                  dense
                  :disable="readonly"
                  @click="rotateItem(-90)"
                >
                  <q-tooltip>Rotate Left</q-tooltip>
                </q-btn>
                <q-btn
                  icon="rotate_right"
                  flat
                  dense
                  :disable="readonly"
                  @click="rotateItem(90)"
                >
                  <q-tooltip>Rotate Right</q-tooltip>
                </q-btn>
              </q-btn-group>
              <span class="text-caption text-grey-7">
                {{ selectedItem.rotation || 0 }}°
              </span>
            </div>
          </div>

          <!-- Size Section -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="straighten" class="q-mr-xs" />
              Size (Fixed)
            </div>
            <div class="row q-gutter-sm">
              <q-input
                :model-value="selectedItem.size?.width"
                label="Width (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                readonly
                disable
              />
              <q-input
                :model-value="selectedItem.size?.height"
                label="Height (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                readonly
                disable
              />
            </div>
            <div class="text-caption text-grey-6 q-mt-xs">
              Controller dimensions are fixed based on model
            </div>
          </div>

          <!-- I/O Summary Section -->
          <div v-if="selectedItem.io_summary" class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="cable" class="q-mr-xs" />
              I/O Summary
            </div>
            <div class="row q-gutter-sm q-mb-sm">
              <div class="col">
                <div class="text-h6 text-center text-primary">{{ selectedItem.io_summary.total || 0 }}</div>
                <div class="text-caption text-center text-grey-7">Capacity</div>
              </div>
              <div class="col">
                <div class="text-h6 text-center text-orange">{{ selectedItem.io_summary.used || 0 }}</div>
                <div class="text-caption text-center text-grey-7">Used</div>
              </div>
              <div class="col">
                <div class="text-h6 text-center text-green">{{ selectedItem.io_summary.available || 0 }}</div>
                <div class="text-caption text-center text-grey-7">Available</div>
              </div>
            </div>
            <!-- I/O Breakdown by Type -->
            <div v-if="selectedItem.io_breakdown && hasIoUsage" class="io-breakdown q-mt-sm">
              <div class="text-caption text-grey-7 q-mb-xs">Breakdown:</div>
              <div class="row q-gutter-xs">
                <q-chip
                  v-for="(count, type) in selectedItem.io_breakdown"
                  v-show="count > 0"
                  :key="type"
                  size="sm"
                  :color="getIoTypeColor(type)"
                  text-color="white"
                  dense
                >
                  {{ type }}: {{ count }}
                </q-chip>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="property-section">
            <q-btn
              label="Remove from Board"
              icon="delete"
              color="negative"
              outline
              class="full-width"
              :disable="readonly"
              @click="$emit('delete-item', selectedItem)"
            />
          </div>
        </q-form>
      </div>

      <!-- Accessory Properties -->
      <div v-else-if="selectedItem.type === 'accessory'" class="item-properties">
        <q-form class="q-pa-md q-gutter-md">
          <!-- Accessory Name -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="electrical_services" class="q-mr-xs" />
              Accessory
            </div>
            <q-input
              :model-value="selectedItem.name"
              label="Name"
              dense
              outlined
              :readonly="readonly"
              @update:model-value="updateProperty('name', $event)"
            >
              <template #prepend>
                <q-icon name="label" color="primary" size="xs" />
              </template>
            </q-input>
          </div>

          <!-- Accessory Type -->
          <div class="property-section">
            <q-input
              :model-value="formatAccessoryType(selectedItem.accessory_type)"
              label="Type"
              dense
              outlined
              readonly
              disable
            >
              <template #prepend>
                <q-icon name="category" color="grey-6" size="xs" />
              </template>
            </q-input>
          </div>

          <!-- Position & Rotation Section -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="place" class="q-mr-xs" />
              Position & Rotation
            </div>
            <div class="row q-gutter-sm q-mb-sm">
              <q-input
                :model-value="Math.round(selectedItem.position?.x || 0)"
                label="X (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updatePosition('x', parseInt($event))"
              />
              <q-input
                :model-value="Math.round(selectedItem.position?.y || 0)"
                label="Y (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updatePosition('y', parseInt($event))"
              />
            </div>
            <div class="row items-center q-gutter-sm">
              <q-btn-group flat>
                <q-btn
                  icon="rotate_left"
                  flat
                  dense
                  :disable="readonly"
                  @click="rotateItem(-90)"
                >
                  <q-tooltip>Rotate Left</q-tooltip>
                </q-btn>
                <q-btn
                  icon="rotate_right"
                  flat
                  dense
                  :disable="readonly"
                  @click="rotateItem(90)"
                >
                  <q-tooltip>Rotate Right</q-tooltip>
                </q-btn>
              </q-btn-group>
              <span class="text-caption text-grey-7">
                {{ selectedItem.rotation || 0 }}°
              </span>
            </div>
          </div>

          <!-- Size Section -->
          <div class="property-section">
            <div class="section-header text-caption text-weight-bold text-grey-7 q-mb-sm">
              <q-icon name="straighten" class="q-mr-xs" />
              Size
            </div>
            <div class="row q-gutter-sm">
              <q-input
                :model-value="selectedItem.size?.width"
                label="Width (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updateSize('width', parseInt($event))"
              />
              <q-input
                :model-value="selectedItem.size?.height"
                label="Height (mm)"
                type="number"
                dense
                outlined
                style="flex: 1"
                :readonly="readonly"
                @update:model-value="updateSize('height', parseInt($event))"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="property-section">
            <q-btn
              label="Remove from Board"
              icon="delete"
              color="negative"
              outline
              class="full-width"
              :disable="readonly"
              @click="$emit('delete-item', selectedItem)"
            />
          </div>
        </q-form>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';

  // Props
  const props = defineProps({
    selectedItem: {
      type: Object,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  });

  // Emits
  const emit = defineEmits(['property-changed', 'delete-item']);

  // Computed
  const hasIoUsage = computed(() => {
    const breakdown = props.selectedItem?.io_breakdown;
    if (!breakdown) {return false;}
    return Object.values(breakdown).some((count) => count > 0);
  });

  // Helper function to get I/O type color
  function getIoTypeColor(type) {
    const colors = {
      AI: 'blue',
      AO: 'cyan',
      DI: 'green',
      DO: 'teal',
      UI: 'purple',
      UO: 'deep-purple',
    };
    return colors[type] || 'grey';
  }

  // Methods
  function updateProperty(property, value) {
    if (props.readonly) {return;}

    emit('property-changed', {
      itemId: props.selectedItem.id,
      property,
      value,
    });
  }

  function updatePosition(axis, value) {
    if (props.readonly) {return;}

    emit('property-changed', {
      itemId: props.selectedItem.id,
      property: 'position',
      value: {
        ...props.selectedItem.position,
        [axis]: value,
      },
    });
  }

  function updateSize(dimension, value) {
    if (props.readonly) {return;}

    emit('property-changed', {
      itemId: props.selectedItem.id,
      property: 'size',
      value: {
        ...props.selectedItem.size,
        [dimension]: value,
      },
    });
  }

  function rotateItem(degrees) {
    if (props.readonly) {return;}

    const currentRotation = props.selectedItem.rotation || 0;
    let newRotation = (currentRotation + degrees) % 360;
    if (newRotation < 0) {newRotation += 360;}

    emit('property-changed', {
      itemId: props.selectedItem.id,
      property: 'rotation',
      value: newRotation,
    });
  }

  function formatAccessoryType(type) {
    if (!type) {return 'Unknown';}
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
</script>

<style scoped lang="scss">
.board-properties-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: white;
  border-left: 1px solid #e0e0e0;
}

.panel-header {
  flex: 0 0 auto;
  border-bottom: 1px solid #e0e0e0;
}

.panel-content {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
}

.no-selection {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.property-section {
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
}

.section-header {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f5f5f5;
  padding: 6px 8px;
  border-radius: 4px;
  margin-bottom: 12px;
}
</style>
